import { Component, inject, input, signal } from '@angular/core';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Booking } from '../../models/booking.model';
import { isWithdrawalEligible } from '../../shared/legal/withdrawal-eligibility.utils';
import { WithdrawalService } from '../../services/withdrawal.service';

@Component({
  selector: 'app-my-bookings-withdrawal',
  imports: [LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './my-bookings-withdrawal.component.html',
})
export class MyBookingsWithdrawalComponent {
  private withdrawalService = inject(WithdrawalService);

  readonly booking = input.required<Booking>();
  readonly spaceCancellationDays = input.required<number>();
  readonly spaceName = input.required<string>();
  readonly formatDate = input.required<(isoDate: string) => string>();

  readonly modalOpen = signal(false);
  readonly acknowledged = signal(false);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);

  isEligible(): boolean {
    const b = this.booking();
    return isWithdrawalEligible(b, this.spaceCancellationDays());
  }

  openModal(event?: Event): void {
    event?.stopPropagation();
    if (!this.isEligible() || this.success()) return;
    this.modalOpen.set(true);
    this.acknowledged.set(false);
    this.error.set(null);
  }

  closeModal(): void {
    if (this.loading()) return;
    this.modalOpen.set(false);
    this.acknowledged.set(false);
    this.error.set(null);
  }

  async confirmWithdrawal(): Promise<void> {
    if (this.loading() || !this.acknowledged()) return;
    const b = this.booking();
    if (!b.id) return;

    this.loading.set(true);
    this.error.set(null);
    try {
      await this.withdrawalService.submitAuthenticated(b.id);
      this.success.set(true);
      this.modalOpen.set(false);
      this.acknowledged.set(false);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const message = (err as { message?: string })?.message;
      if (code === 'functions/already-exists') {
        this.error.set('Ya existe una solicitud de desistimiento para esta reserva.');
      } else if (message) {
        this.error.set(message);
      } else {
        this.error.set('No se pudo enviar la solicitud. Inténtalo de nuevo.');
      }
    } finally {
      this.loading.set(false);
    }
  }
}
